import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dropdown, Modal } from "react-bootstrap";
import {
  getMyNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  getSentNotifications,
  sendNotification,
  deleteNotification,
} from "../../../services/NotificationService";
import { getUsers } from "../../../services/UserService";
import DataTablePagination from "../common/DataTablePagination";
import { Trans, useTranslation } from "react-i18next";
import { usePermissions, PERMISSIONS } from "../../../utils/permissions";

import i18n from "../../../i18n";
import { getLocale } from "../../../utils/i18nLabels";
const typeOptions = [
  { value: "system", get label() { return i18n.t("common.notificationType.system"); } },
  { value: "exam_published", get label() { return i18n.t("common.notificationType.exam_published"); } },
  { value: "exam_session_scheduled", get label() { return i18n.t("common.notificationType.exam_session_scheduled"); } },
  { value: "result_available", get label() { return i18n.t("common.notificationType.result_available"); } },
];

const emptyForm = { title: "", body: "", type: "system", target: "broadcast", role: "STUDENT", user_ids: [] };

const Notifications = () => {
  const { t } = useTranslation();
  const can = usePermissions();
  // Mêmes permissions que le backend : POST / (NOTIFICATION_CREATE),
  // GET / = liste des envoyées (NOTIFICATION_LIST), DELETE (NOTIFICATION_DELETE).
  const canSend = can(PERMISSIONS.NOTIFICATION_CREATE);
  const canViewSent = can(PERMISSIONS.NOTIFICATION_LIST);
  const canDelete = can(PERMISSIONS.NOTIFICATION_DELETE);

  const [tab, setTab] = useState("mine");

  // --- Mes notifications ---
  const [mine, setMine] = useState([]);
  const [mineLoading, setMineLoading] = useState(false);
  const [minePage, setMinePage] = useState(1);
  const [mineLimit] = useState(10);
  const [mineTotal, setMineTotal] = useState(0);
  const [mineTotalPages, setMineTotalPages] = useState(1);
  const [mineFilter, setMineFilter] = useState("");
  const [feedback, setFeedback] = useState(null);

  const fetchMine = useCallback(async () => {
    setMineLoading(true);
    try {
      const params = { page: minePage, limit: mineLimit };
      if (mineFilter !== "") params.is_read = mineFilter;
      const res = await getMyNotifications(params);
      setMine(res.data?.data || []);
      setMineTotal(res.data?.pagination?.total ?? 0);
      setMineTotalPages(res.data?.pagination?.totalPages ?? 1);
    } catch (err) {
      setFeedback({ type: "danger", message: t("pages.notifications.loadMineError") });
    } finally {
      setMineLoading(false);
    }
  }, [minePage, mineLimit, mineFilter, t]);

  useEffect(() => {
    fetchMine();
  }, [fetchMine]);

  const handleMarkRead = async (id) => {
    try {
      await markNotificationRead(id);
      setMine((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    } catch (err) {
      setFeedback({ type: "danger", message: t("pages.notifications.markReadError") });
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setMine((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch (err) {
      setFeedback({ type: "danger", message: t("pages.notifications.markAllReadError") });
    }
  };

  // --- Notifications envoyées (admin) ---
  const [sent, setSent] = useState([]);
  const [sentLoading, setSentLoading] = useState(false);
  const [sentPage, setSentPage] = useState(1);
  const [sentLimit] = useState(10);
  const [sentTotal, setSentTotal] = useState(0);
  const [sentTotalPages, setSentTotalPages] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchSent = useCallback(async () => {
    if (!canViewSent) return;
    setSentLoading(true);
    try {
      const res = await getSentNotifications({ page: sentPage, limit: sentLimit });
      setSent(res.data?.data || []);
      setSentTotal(res.data?.pagination?.total ?? 0);
      setSentTotalPages(res.data?.pagination?.totalPages ?? 1);
    } catch (err) {
      setFeedback({ type: "danger", message: t("pages.notifications.loadSentError") });
    } finally {
      setSentLoading(false);
    }
  }, [canViewSent, sentPage, sentLimit, t]);

  useEffect(() => {
    if (tab === "sent") fetchSent();
  }, [tab, fetchSent]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteNotification(deleteTarget.id);
      setFeedback({ type: "success", message: t("pages.notifications.deleteSuccess") });
      setDeleteTarget(null);
      fetchSent();
    } catch (err) {
      setFeedback({ type: "danger", message: t("pages.notifications.deleteError") });
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  // --- Formulaire d'envoi ---
  const [showSendModal, setShowSendModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (canSend) {
      getUsers({ page: 1, limit: 200 }).then((res) => setUsers(res.data?.data || [])).catch(() => {});
    }
  }, [canSend]);

  const openSendModal = () => {
    setFormData(emptyForm);
    setFormErrors({});
    setSubmitError("");
    setShowSendModal(true);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const toggleUser = (userId) => {
    setFormData((prev) => {
      const has = prev.user_ids.includes(userId);
      return { ...prev, user_ids: has ? prev.user_ids.filter((id) => id !== userId) : [...prev.user_ids, userId] };
    });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.title.trim()) errors.title = t("common.ui.leTitreEstRequis");
    if (formData.target === "users" && formData.user_ids.length === 0)
      errors.user_ids = t("pages.notifications.recipientsRequired");
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const payload = {
        title: formData.title.trim(),
        body: formData.body.trim() || null,
        type: formData.type,
      };
      if (formData.target === "broadcast") payload.broadcast = true;
      else if (formData.target === "role") payload.role = formData.role;
      else payload.user_ids = formData.user_ids;

      await sendNotification(payload);
      setFeedback({ type: "success", message: t("pages.notifications.sendSuccess") });
      setShowSendModal(false);
      if (tab === "sent") fetchSent();
    } catch (err) {
      setSubmitError(err?.response?.data?.message || t("pages.notifications.sendError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">{t("menu.dashboard.title")}</Link>
                </li>
                <li className="breadcrumb-item active">{t("menu.notifications")}</li>
              </ol>
            </nav>
          </div>
          {canSend && (
            <div className="mb-2">
              <button type="button" className="btn btn-primary d-flex align-items-center" onClick={openSendModal}>
                <i className="ti ti-send me-2"></i>{t("pages.notifications.sendLabel")}
              </button>
            </div>
          )}
        </div>

        {feedback && (
          <div className={`alert alert-${feedback.type} alert-dismissible fade show`} role="alert">
            {feedback.message}
            <button type="button" className="btn-close" onClick={() => setFeedback(null)}></button>
          </div>
        )}

        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button type="button" className={`nav-link ${tab === "mine" ? "active" : ""}`} onClick={() => setTab("mine")}>
              {t("pages.notifications.myNotificationsTab")}
            </button>
          </li>
          {canViewSent && (
            <li className="nav-item">
              <button type="button" className={`nav-link ${tab === "sent" ? "active" : ""}`} onClick={() => setTab("sent")}>
                {t("pages.notifications.sentNotificationsTab")}
              </button>
            </li>
          )}
        </ul>

        {tab === "mine" && (
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">{t("pages.notifications.myNotificationsTab")}</h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="mb-3 me-2">
                  <select
                    className="form-select"
                    value={mineFilter}
                    onChange={(e) => {
                      setMineFilter(e.target.value);
                      setMinePage(1);
                    }}
                  >
                    <option value="">{t("pages.notifications.all")}</option>
                    <option value="false">{t("pages.notifications.unread")}</option>
                    <option value="true">{t("pages.notifications.read")}</option>
                  </select>
                </div>
                <div className="mb-3">
                  <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleMarkAllRead}>
                    {t("pages.notifications.markAllRead")}
                  </button>
                </div>
              </div>
            </div>
            <div className="card-body p-0 py-3">
              {mineLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{t("common.crud.loading")}</span>
                  </div>
                </div>
              ) : mine.length > 0 ? (
                <div className="list-group list-group-flush">
                  {mine.map((notif) => (
                    <div
                      key={notif.id}
                      className={`list-group-item d-flex justify-content-between align-items-start ${!notif.is_read ? "bg-light" : ""}`}
                    >
                      <div>
                        <div className="d-flex align-items-center gap-2">
                          {!notif.is_read && <span className="badge bg-primary">{t("pages.notifications.newBadge")}</span>}
                          <strong>{notif.title}</strong>
                        </div>
                        {notif.body && <p className="mb-1 text-muted">{notif.body}</p>}
                        <small className="text-muted">{new Date(notif.created_at).toLocaleString(getLocale())}</small>
                      </div>
                      {!notif.is_read && (
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => handleMarkRead(notif.id)}>
                          {t("pages.notifications.markRead")}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <i className="ti ti-bell-off fs-1 text-muted"></i>
                  <h5 className="mt-2">{t("header.notifications.empty")}</h5>
                </div>
              )}
              <DataTablePagination
                variant="footer"
                page={minePage}
                limit={mineLimit}
                total={mineTotal}
                totalPages={mineTotalPages}
                onPageChange={setMinePage}
                onLimitChange={() => {}}
              />
            </div>
          </div>
        )}

        {tab === "sent" && canViewSent && (
          <div className="card">
            <div className="card-header">
              <h4 className="mb-0">{t("pages.notifications.sentNotificationsTab")}</h4>
            </div>
            <div className="card-body p-0 py-3">
              <div className="table-responsive">
                <table className="table datatable">
                  <thead className="thead-light">
                    <tr>
                      <th>{t("common.ui.titre")}</th>
                      <th>{t("common.fields.type")}</th>
                      <th>{t("pages.notifications.sentAt")}</th>
                      <th>{t("common.crud.actionColumn")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sentLoading ? (
                      <tr>
                        <td colSpan="4" className="text-center py-4">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">{t("common.crud.loading")}</span>
                          </div>
                        </td>
                      </tr>
                    ) : sent.length > 0 ? (
                      sent.map((notif) => (
                        <tr key={notif.id}>
                          <td className="fw-medium">{notif.title}</td>
                          <td>
                            <span className="badge badge-soft-info">
                              {typeOptions.find((t) => t.value === notif.type)?.label || notif.type}
                            </span>
                          </td>
                          <td>{new Date(notif.created_at).toLocaleString(getLocale())}</td>
                          <td>
                            {canDelete && (
                            <Dropdown>
                              <Dropdown.Toggle
                                as="button"
                                className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0 i-false"
                              >
                                <i className="ti ti-dots-vertical fs-14"></i>
                              </Dropdown.Toggle>
                              <Dropdown.Menu align="end" className="p-3">
                                <Dropdown.Item className="rounded-1" onClick={() => setDeleteTarget(notif)}>
                                  <i className="ti ti-trash-x me-2"></i>{t("common.crud.delete")}
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center py-4">
                          {t("pages.notifications.noNotificationsSent")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <DataTablePagination
                variant="footer"
                page={sentPage}
                limit={sentLimit}
                total={sentTotal}
                totalPages={sentTotalPages}
                onPageChange={setSentPage}
                onLimitChange={() => {}}
              />
            </div>
          </div>
        )}
      </div>

      <Modal show={showSendModal} onHide={() => (submitting ? null : setShowSendModal(false))} centered size="lg">
        <form onSubmit={handleSend}>
          <Modal.Header closeButton>
            <Modal.Title>{t("pages.notifications.sendLabel")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {submitError && <div className="alert alert-danger">{submitError}</div>}
            <div className="mb-3">
              <label className="form-label">
                {t("common.ui.titre")} <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${formErrors.title ? "is-invalid" : ""}`}
                value={formData.title}
                onChange={(e) => handleFormChange("title", e.target.value)}
              />
              {formErrors.title && <div className="invalid-feedback">{formErrors.title}</div>}
            </div>
            <div className="mb-3">
              <label className="form-label">{t("pages.notifications.messageLabel")}</label>
              <textarea
                className="form-control"
                rows={3}
                value={formData.body}
                onChange={(e) => handleFormChange("body", e.target.value)}
              ></textarea>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("common.fields.type")}</label>
                <select className="form-select" value={formData.type} onChange={(e) => handleFormChange("type", e.target.value)}>
                  {typeOptions.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("pages.notifications.recipientsLabel")}</label>
                <select className="form-select" value={formData.target} onChange={(e) => handleFormChange("target", e.target.value)}>
                  <option value="broadcast">{t("pages.notifications.allUsers")}</option>
                  <option value="role">{t("pages.notifications.specificRole")}</option>
                  <option value="users">{t("pages.notifications.specificUsers")}</option>
                </select>
              </div>
            </div>
            {formData.target === "role" && (
              <div className="mb-3">
                <label className="form-label">{t("common.fields.role")}</label>
                <select className="form-select" value={formData.role} onChange={(e) => handleFormChange("role", e.target.value)}>
                  <option value="STUDENT">{t("roles.STUDENT")}</option>
                  <option value="TEACHER">{t("roles.TEACHER")}</option>
                  <option value="ADMIN">{t("roles.ADMIN")}</option>
                </select>
              </div>
            )}
            {formData.target === "users" && (
              <div className="mb-3">
                <label className="form-label">
                  {t("common.fields.users")} <span className="text-danger">*</span>
                </label>
                {formErrors.user_ids && <div className="text-danger small mb-2">{formErrors.user_ids}</div>}
                <div style={{ maxHeight: "200px", overflowY: "auto" }} className="border rounded p-2">
                  {users.map((u) => (
                    <div className="form-check" key={u.id}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`notif-user-${u.id}`}
                        checked={formData.user_ids.includes(u.id)}
                        onChange={() => toggleUser(u.id)}
                      />
                      <label className="form-check-label" htmlFor={`notif-user-${u.id}`}>
                        {[u.first_name, u.last_name].filter(Boolean).join(" ") || u.email} ({u.email})
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-light" onClick={() => setShowSendModal(false)} disabled={submitting}>
              {t("common.crud.cancel")}
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t("pages.notifications.sending") : t("pages.notifications.send")}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal show={deleteTarget !== null} onHide={() => (deleting ? null : setDeleteTarget(null))} centered>
        <div className="modal-body text-center">
          <span className="delete-icon">
            <i className="ti ti-trash-x"></i>
          </span>
          <h4>{t("common.crud.confirmDeleteTitle")}</h4>
          <p>
            <Trans i18nKey="pages.notifications.voulezVousVraimentSupprimerNotificationElle" values={{ title: deleteTarget?.title }} components={[<strong />]} />
          </p>
          <div className="d-flex justify-content-center">
            <button type="button" className="btn btn-light me-3" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              {t("common.crud.cancel")}
            </button>
            <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={deleting}>
              {deleting ? t("common.crud.deleting") : t("common.crud.confirmDelete")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Notifications;
